import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Avatar,
  Snackbar,
  IconButton
} from '@mui/material';
import {
  Phone,
  Email,
  LocationOn,
  AccessTime,
  Send,
  WhatsApp,
  Facebook,
  Twitter,
  LinkedIn,
  ContentCopy,
  CheckCircle
} from '@mui/icons-material';
import { requestService } from '../services/api';
import { red } from '@mui/material/colors';

const Contact = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    serviceType: 'contact'
  });
  
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('fade-in-up');
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.animate-on-scroll').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      await requestService.submitRequest(formData);
      setSuccess(true);
      setFormData({
        fullName: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
        serviceType: 'contact'
      });
      
      setTimeout(() => {
        setSuccess(false);
      }, 5000);
    } catch (err) {
      setError(err.response?.data?.message || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  const contactInfo = [
    { 
      icon: <LocationOn sx={{ fontSize: 32 }} />, 
      title: 'Adresse', 
      details: ['Tokoin Forever', 'Lomé, Togo'],
      color: '#1a237e',
      copyText: 'Tokoin Forever, Lomé - Togo'
    },
    { 
      icon: <Phone sx={{ fontSize: 32 }} />, 
      title: 'Téléphones', 
      details: ['+228 90 35 87 66', '+228 99 54 90 41'],
      color: '#ff9800',
      copyText: '+228 90 35 87 66'
    },
    { 
      icon: <Email sx={{ fontSize: 32 }} />, 
      title: 'Email', 
      details: ['aujaagroup14@gmail.com'],
      color: '#1a237e',
      copyText: 'aujaagroup14@gmail.com'
    },
    { 
      icon: <AccessTime sx={{ fontSize: 32 }} />, 
      title: 'Horaires', 
      details: ['Lundi - Dimanche: 8h - 20h', 'Service en ligne: 24h/24'],
      color: '#ff9800',
    },
  ];

  const socialLinks = [
    { icon: <WhatsApp />, url: 'https://wa.me/22890358766', color: '#25D366', label: 'WhatsApp' },
    { icon: <Facebook />, url: 'https://facebook.com', color: '#1877F2', label: 'Facebook' },
    { icon: <Twitter />, url: 'https://twitter.com', color: '#1DA1F2', label: 'Twitter' },
    { icon: <LinkedIn />, url: 'https://linkedin.com', color: '#0077B5', label: 'LinkedIn' },
  ];

  return (
    <Container maxWidth="lg">
      <Box sx={{ textAlign: 'center', mb: 6, mt: 4 }} className="fade-in-up">
        <Typography 
          variant="h2" 
          gutterBottom 
          fontWeight="bold"
          sx={{ 
            fontSize: { xs: '2rem', md: '3rem' },
          }}
        >
          Contactez-nous
        </Typography>
        <Typography variant="h6" color="text.secondary" maxWidth="600px" margin="auto">
          Notre équipe est à votre écoute pour répondre à toutes vos questions
        </Typography>
      </Box>

      <Snackbar
        open={copySuccess}
        autoHideDuration={2000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" sx={{ width: '100%' }}>
          Copié dans le presse-papier !
        </Alert>
      </Snackbar>

      <Grid container spacing={4}>

        {/* Formulaire de contact */}
        <Grid size={{ xs: 12, md: 7 }}>
          <Paper 
            elevation={1} 
            className="animate-on-scroll"
            sx={{ 
              p: { xs: 3, md: 4 },
              borderRadius: 4,
              transition: 'all 0.3s ease',
            }}
          >
            <Typography variant="h4" gutterBottom fontWeight="bold" color="primary" sx={{ mb: 3 }}>
              Envoyez-nous un message
            </Typography>

            {success && (
              <Alert severity="success" sx={{ mb: 3 }} icon={<CheckCircle />}>
                ✅ Votre message a été envoyé avec succès. Nous vous répondrons rapidement.
              </Alert>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                ❌ {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit} noValidate>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    required
                    fullWidth
                    label="Nom complet"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      },
                    }}
                  />
                </Grid>
                
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    required
                    fullWidth
                    label="Email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      },
                    }}
                  />
                </Grid>
                
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    required
                    fullWidth
                    label="Téléphone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      },
                    }}
                  />
                </Grid>
                
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    required
                    fullWidth
                    label="Sujet"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    variant="outlined"
                    placeholder="Ex: Demande d'information sur..."
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      },
                    }}
                  />
                </Grid>
                
                <Grid size={{ xs: 12 }}>
                  <TextField
                    required
                    fullWidth
                    multiline
                    rows={6}
                    label="Votre message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Décrivez votre demande en détail..."
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      },
                    }}
                  />
                </Grid>
                
                <Grid size={{ xs: 12 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    size="large"
                    fullWidth
                    disabled={loading}
                    sx={{ 
                      py: 1.5,
                      borderRadius: 50,
                      fontSize: '1rem',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                      },
                    }}
                  >
                    {loading ? <CircularProgress size={24} /> : (
                      <>
                        <Send sx={{ mr: 1 }} />
                        Envoyer le message
                      </>
                    )}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Grid>

        {/* Informations de contact */}
        <Grid size={{ xs: 12, md: 5 }}>
          <Box className="animate-on-scroll">
            <Card elevation={1} sx={{ borderRadius: 4, mb: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h5" gutterBottom fontWeight="bold" color="primary" sx={{ mb: 1 }}>
                  Informations de contact
                </Typography>
                
                {contactInfo.map((info, index) => (
                  <Box 
                    key={index}
                    sx={{ 
                      display: 'flex', 
                      mb: 1,
                      p: 1,
                      borderRadius: 2,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        backgroundColor: 'rgba(26,35,126,0.05)',
                      },
                    }}
                  >
                    <Avatar sx={{ bgcolor: info.color, mr: 2, width: 56, height: 56 }}>
                      {info.icon}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {info.title}
                      </Typography>
                      {info.details.map((detail, idx) => (
                        <Typography key={idx} variant="body2" color="text.secondary">
                          {detail}
                        </Typography>
                      ))}
                      {info.copyText && (
                        <IconButton 
                          size="small" 
                          onClick={() => copyToClipboard(info.copyText)}
                          sx={{ mt: 0.7, p: 0.5 }}
                        >
                          <ContentCopy fontSize="small" />
                          <Typography variant="caption" sx={{ ml: 0.5 }}>
                            Copier
                          </Typography>
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Box>
        </Grid>

        {/* Pourquoi nous choisir */}
        <Grid size={{ xs: 12, md: 7 }}>
            <Card elevation={1} sx={{ borderRadius: 4, backgroundColor: '#fff3e0',mb:3 }}>
              <CardContent sx={{ p: 3}}>
                <Typography variant="h5" gutterBottom fontWeight="bold" color="primary" sx={{ mb: 1 }}>
                  Pourquoi nous choisir ?
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {[
                    '✓ Experts juridiques expérimentés',
                    '✓ Disponibilité 7j/7',
                    '✓ Services adaptés à la diaspora',
                    '✓ Confidentialité garantie',
                    '✓ Tarifs transparents',
                    '✓ Réponse rapide sous 24h',
                  ].map((item, index) => (
                    <Typography key={index} variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                      {item}
                    </Typography>
                  ))}
                </Box>
              </CardContent>
            </Card>
        </Grid>

         {/* Réseaux sociaux */}
        <Grid size={{ xs: 12, md: 5 }}>
            <Card elevation={1} sx={{ borderRadius: 4, mb: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h5" gutterBottom fontWeight="bold" color="primary" sx={{ mb: 3 }}>
                  Suivez-nous
                </Typography>
                <Box sx={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                  {socialLinks.map((social, index) => (
                    <IconButton
                      key={index}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        backgroundColor: social.color,
                        color: 'white',
                        width: 50,
                        height: 50,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-5px)',
                          boxShadow: 4,
                        },
                      }}
                    >
                      {social.icon}
                    </IconButton>
                  ))}
                </Box>
              </CardContent>
            </Card>
        </Grid>

      </Grid>
    </Container>
  );
};

export default Contact;